import { useState, useCallback } from 'react';
import { testIds } from '../../lib/testIds';
import { useAppDispatch, useAppSelector } from '../../store';
import {
  addSkillPrompt,
  removeSkillPrompt,
  updateSkillPrompt,
  type SkillPrompt,
} from '@repancy/core';
import { SkillPicker } from '../skills/SkillPicker';
import { SaveAsSkillDialog } from '../skills/SaveAsSkillDialog';
import { HelpTip } from '../ui/HelpTip';

interface SkillPromptSlotsProps {
  sessionId: string;
}

const EMPTY_SKILL_PROMPTS: readonly SkillPrompt[] = Object.freeze([]);

const cardStyle: React.CSSProperties = {
  borderStyle: 'solid',
  background: '#ece9d8',
  padding: 6,
  marginBottom: 4,
  fontFamily: "var(--xp-font-body)",
  fontSize: 11,
};

const headerRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  cursor: 'pointer',
  userSelect: 'none',
};

const smallBtnStyle: React.CSSProperties = {
  fontFamily: "var(--xp-font-body)",
  fontSize: 11,
  padding: '2px 8px',
  background: '#ece9d8',
  color: '#000',
  borderStyle: 'solid',
  cursor: 'pointer',
};

const textareaStyle: React.CSSProperties = {
  width: '100%',
  minHeight: 80,
  resize: 'vertical',
  fontFamily: "var(--xp-font-mono)",
  fontSize: 11,
  lineHeight: 1.4,
  padding: 4,
  background: '#fff',
  color: '#000',
  borderStyle: 'solid',
  borderWidth: 2,
  outline: 'none',
  boxSizing: 'border-box' as const,
  marginTop: 4,
};

let skillIdCounter = 0;
function nextSkillId(): string {
  skillIdCounter += 1;
  return `skill-${Date.now()}-${skillIdCounter}`;
}

export function SkillPromptSlots({ sessionId }: SkillPromptSlotsProps) {
  const dispatch = useAppDispatch();
  const skillPrompts = useAppSelector(
    (s) => s.promptSessions.sessions.entities[sessionId]?.skillPrompts ?? EMPTY_SKILL_PROMPTS,
  );
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [showPicker, setShowPicker] = useState(false);
  const [savingFromId, setSavingFromId] = useState<string | null>(null);

  const toggleExpand = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handlePicked = useCallback(
    (
      sp: SkillPrompt,
      meta: { skillId: string; skillVersionNumber: number },
    ) => {
      const withMeta: SkillPrompt = {
        ...sp,
        id: sp.id || nextSkillId(),
        skillId: meta.skillId,
        skillVersionNumber: meta.skillVersionNumber,
      };
      dispatch(addSkillPrompt({ sessionId, skillPrompt: withMeta }));
      setShowPicker(false);
    },
    [dispatch, sessionId],
  );

  const handleAddCustom = useCallback(() => {
    const sp: SkillPrompt = {
      id: nextSkillId(),
      name: 'Custom Skill',
      content: '',
      weight: 1,
      enabled: true,
    };
    dispatch(addSkillPrompt({ sessionId, skillPrompt: sp }));
    setShowPicker(false);
  }, [dispatch, sessionId]);

  const handleRemove = useCallback(
    (skillPromptId: string) => {
      dispatch(removeSkillPrompt({ sessionId, skillPromptId }));
    },
    [dispatch, sessionId],
  );

  const handleToggleEnabled = useCallback(
    (skillPromptId: string, enabled: boolean) => {
      dispatch(updateSkillPrompt({ sessionId, skillPromptId, changes: { enabled } }));
    },
    [dispatch, sessionId],
  );

  const handleWeightChange = useCallback(
    (skillPromptId: string, weight: number) => {
      dispatch(updateSkillPrompt({ sessionId, skillPromptId, changes: { weight } }));
    },
    [dispatch, sessionId],
  );

  const handleContentChange = useCallback(
    (skillPromptId: string, content: string) => {
      dispatch(updateSkillPrompt({ sessionId, skillPromptId, changes: { content } }));
    },
    [dispatch, sessionId],
  );

  const handleDisconnect = useCallback(
    (skillPromptId: string) => {
      dispatch(
        updateSkillPrompt({
          sessionId,
          skillPromptId,
          changes: { skillId: undefined, skillVersionNumber: undefined },
        }),
      );
    },
    [dispatch, sessionId],
  );

  return (
    <div style={{ marginBottom: 8 }} data-testid={testIds.promptLab.skillSlots}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 6,
        }}
      >
        <span
          style={{
            fontFamily: "var(--xp-font-body)",
            fontSize: 12,
            fontWeight: 'bold',
            color: '#000',
          }}
        >
          Skill Prompts
        </span>
        <HelpTip k="promptLab.addSkill" wrap>
          <button
            style={smallBtnStyle}
            onClick={() => setShowPicker(true)}
            data-testid={testIds.promptLab.addSkillButton}
          >
            Add Skill
          </button>
        </HelpTip>
      </div>

      {skillPrompts.length === 0 && (
        <div
          style={{
            ...cardStyle,
            color: '#808080',
            fontStyle: 'italic',
            textAlign: 'center',
            padding: 12,
          }}
        >
          No skill prompts added. Click "Add Skill" to compose your system prompt.
        </div>
      )}

      {skillPrompts.map((sp) => {
        const isExpanded = expandedIds.has(sp.id);
        const isFromLibrary =
          typeof sp.skillId === 'string' &&
          sp.skillId.length > 0 &&
          typeof sp.skillVersionNumber === 'number';
        return (
          <div key={sp.id} style={cardStyle} data-testid={testIds.promptLab.skillCard(sp.id)}>
            <div style={headerRowStyle} onClick={() => toggleExpand(sp.id)} data-testid={testIds.promptLab.skillExpand(sp.id)}>
              {/* Drag handle (visual only) */}
              <span style={{ cursor: 'grab', fontSize: 14, color: '#808080' }}>
                &#x2630;
              </span>
              <input
                type="checkbox"
                checked={sp.enabled}
                onChange={(e) => {
                  e.stopPropagation();
                  handleToggleEnabled(sp.id, e.target.checked);
                }}
                style={{ marginRight: 2 }}
                data-testid={testIds.promptLab.skillEnable(sp.id)}
              />
              <span
                style={{
                  flex: 1,
                  fontWeight: 'bold',
                  color: sp.enabled ? '#000' : '#808080',
                  textDecoration: sp.enabled ? 'none' : 'line-through',
                }}
              >
                {sp.name}
              </span>
              {isFromLibrary && (
                <>
                  <span
                    data-testid={testIds.promptLab.skillVersionChip(sp.id)}
                    title="Linked to firm Skill Library — Disconnect to fork into a one-off"
                    style={{
                      fontSize: 9,
                      padding: '0 4px',
                      background: '#e0e0ff',
                      border: '1px solid var(--xp-selection)',
                      color: 'var(--xp-selection)',
                      fontFamily: "var(--xp-font-mono), 'Consolas', monospace",
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {`${sp.skillId!.slice(0, 8)}@v${sp.skillVersionNumber!}`}
                  </span>
                  <button
                    data-testid={testIds.promptLab.skillDisconnect(sp.id)}
                    title="Disconnect from library so edits create a one-off (audit shows the fork)"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDisconnect(sp.id);
                    }}
                    style={{
                      ...smallBtnStyle,
                      padding: '1px 6px',
                      fontSize: 10,
                    }}
                  >
                    Disconnect
                  </button>
                </>
              )}
              <span style={{ fontSize: 10, color: '#808080', marginRight: 4 }}>
                {isExpanded ? '▲' : '▼'}
              </span>
              <button
                style={{
                  ...smallBtnStyle,
                  padding: '1px 6px',
                  fontSize: 10,
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setSavingFromId(sp.id);
                }}
                title="Save this prompt as a Skill in the firm library"
              >
                Save…
              </button>
              <button
                style={{
                  ...smallBtnStyle,
                  padding: '1px 6px',
                  fontSize: 10,
                  color: '#800000',
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemove(sp.id);
                }}
                data-testid={testIds.promptLab.skillRemove(sp.id)}
              >
                X
              </button>
            </div>

            {/* Weight slider */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                marginTop: 4,
                fontSize: 10,
              }}
            >
              <span>Weight:</span>
              <input
                type="range"
                min={0}
                max={1}
                step={0.05}
                value={sp.weight}
                onChange={(e) => handleWeightChange(sp.id, parseFloat(e.target.value))}
                style={{ flex: 1 }}
                data-testid={testIds.promptLab.skillWeight(sp.id)}
              />
              <span style={{ minWidth: 28, textAlign: 'right' }}>
                {sp.weight.toFixed(2)}
              </span>
            </div>

            {/* Expanded content editor */}
            {isExpanded && (
              <textarea
                style={textareaStyle}
                value={sp.content}
                onChange={(e) => handleContentChange(sp.id, e.target.value)}
                data-testid={testIds.promptLab.skillContent(sp.id)}
              />
            )}
          </div>
        );
      })}

      {showPicker && (
        <SkillPicker
          onPick={(sp, meta) => handlePicked(sp, meta)}
          onCancel={() => setShowPicker(false)}
        />
      )}
      {showPicker && (
        // Sit below the demo banner (z-9999, ~30px tall) so the demo
        // strip can't intercept clicks. Z-index above any window frame
        // chrome. Position top: 56 ensures clearance even on touch.
        <div style={{ position: 'fixed', top: 56, right: 16, zIndex: 10001 }}>
          <button
            style={smallBtnStyle}
            onClick={handleAddCustom}
            data-testid="skill-add-custom"
          >
            + Custom (skip library)
          </button>
        </div>
      )}
      {savingFromId !== null && (
        <SaveAsSkillDialog
          initialBody={
            skillPrompts.find((sp) => sp.id === savingFromId)?.content ?? ''
          }
          onSaved={() => setSavingFromId(null)}
          onCancel={() => setSavingFromId(null)}
        />
      )}
    </div>
  );
}
